import {
  DOWN_KEYCODE,
  ENTER_KEYCODE,
  SelectStateContext,
  SPACE_KEYCODE,
  UP_KEYCODE,
} from 'app/components/forms/SelectState/constants';
import { eventHasModifierKeys } from 'app/utils/env';
import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

export interface IOptionControlProps {
  'aria-posinset': number;
  'aria-setsize': number;
  onClick: (event: React.MouseEvent) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onMouseMove: () => void;
  ref: (node: HTMLElement | null) => void;
  role: string;
  tabIndex: number;
}

interface IOptionProps {
  children: (props: IOptionControlProps) => ReactElement<any> | null;
  id: string;
}

/**
 * `<Option>` is responsible for:
 *
 * 1. Triggering `SelectState.select()` on certain events (e.g. click, spacebar/enter key down)
 * 2. Causing both its DOM element to be `.focus()`ed `SelectState.state.focusedId` to be updated
 *    when the focus is changed (either via hovering or up/down arrow keys).
 */
const Option = ({ children, id }: IOptionProps) => {
  const selectStateContext = useContext(SelectStateContext);

  const index = useMemo(() => {
    return selectStateContext.optionIds.indexOf(id);
  }, [selectStateContext.optionIds, id]);

  const ref = useRef<HTMLElement | null>(null);
  const callbackRef = useCallback((node: HTMLElement | null) => {
    ref.current = node;
  }, []);

  useEffect(() => {
    if (selectStateContext.focusedId === id && selectStateContext.isOpen && ref.current) {
      ref.current.focus();
    }
  }, [id, selectStateContext.focusedId, selectStateContext.isOpen]);

  const select = () => {
    selectStateContext.select(id);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!eventHasModifierKeys(event)) {
      const { keyCode } = event;

      if (keyCode === ENTER_KEYCODE) {
        event.preventDefault();
        select();
      } else if (keyCode === SPACE_KEYCODE) {
        // Don't allow selecting on spacebar, because this may intefere with typing text to jump to
        // a matching option and cause confusion.
        //
        // However, still prevent spacebar's default behavior of scrolling down.
        event.preventDefault();
      } else if (keyCode === UP_KEYCODE) {
        event.preventDefault();
        selectStateContext.focusPrevious();
      } else if (keyCode === DOWN_KEYCODE) {
        event.preventDefault();
        selectStateContext.focusNext();
      }
    }
  };

  const onClick = (event: React.MouseEvent) => {
    const { nativeEvent } = event;

    if (
      selectStateContext.isOpen &&
      // Ignore click events generated by space key presses in Firefox, because if you open the
      // menu with the space key, on key up you will "click" the currently selected option and
      // immediately close the menu.
      !(hasMozInputSource(nativeEvent) && nativeEvent.mozInputSource === 6) /* MOZ_SOURCE_KEYBOARD */
    ) {
      event.preventDefault();
      select();
    }
  };

  // Use the mousemove event instead of useHoverState (which uses enter/leave events), to avoid the
  // focus being set while scrolling, which can cause the scrolling to jump (and can be bad for
  // performance).
  const onMouseMove = () => {
    if (selectStateContext.isOpen) {
      selectStateContext.setFocusedId(id);
    }
  };

  return children({
    'aria-posinset': index + 1,
    'aria-setsize': selectStateContext.optionIds.size,
    onClick,
    onKeyDown,
    onMouseMove,
    ref: callbackRef,
    role: 'option',
    // Don't allow tabbing between options. Tab keys should change focus for the entire select
    // dialog, and up/down arrow keys should change focus for individual options.
    tabIndex: -1,
  });
};

const hasMozInputSource = (event: MouseEvent): event is MouseEvent & { mozInputSource: number } => {
  return 'mozInputSource' in event;
};

export default Option;
